import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ModalWrapper, ModalCloseButton, TextBox, Select, Button, CheckBox } from '../common';
import { partnerService } from '../../api/services';

export default function PartnerApplyModal({ onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    type: '',
    name: '',
    company: '',
    phone: '',
    email: '',
    content: '',
  });
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const partnerTypes = [
    { value: '', label: t('common.partner_type_select_placeholder') },
    { value: 'interpreter', label: t('common.partner_type_interpreter') },
    { value: 'callvan', label: t('common.partner_type_callvan') },
    { value: 'driver', label: t('common.partner_type_driver') },
    { value: 'other', label: t('common.partner_type_other') },
  ];

  // 일반 input/textarea 핸들러 — e가 undefined로 호출되는 케이스도 방어
  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: (e?.target?.value ?? e) ?? '' }));

  // 전화번호는 react-phone-number-input이 이벤트 객체가 아닌 string 또는 undefined를 직접 전달.
  // 국가 코드 드롭다운만 누르고 빈 상태로 콜백되는 경우(undefined) 일반 update에서
  // e.target 접근 시 TypeError 발생 → 별도 핸들러 사용.
  const handlePhoneChange = (value) =>
    setForm((prev) => ({ ...prev, phone: value ?? '' }));

  const isValid =
    form.type && form.name && form.phone && form.email && form.content && privacyAgreed;

  const handleApply = async () => {
    if (submitting) return;
    if (!form.type) return alert(t('common.partner_alert_type'));
    if (!form.name?.trim()) return alert(t('common.partner_alert_name'));
    if (!form.phone) return alert(t('common.partner_alert_phone'));
    if (!form.email?.trim()) return alert(t('common.partner_alert_email'));
    if (!form.content?.trim()) return alert(t('common.partner_alert_content'));
    if (!privacyAgreed) return alert(t('common.partner_alert_privacy'));

    setSubmitting(true);
    try {
      // BE 컬럼명: `introduction` (Text). FE 폼 라벨은 "문의 내용" 그대로 유지하되 전송 키만 일치.
      await partnerService.apply({
        partner_type: form.type,
        name: form.name.trim(),
        company: form.company?.trim() || null,
        phone: form.phone,
        email: form.email.trim(),
        introduction: form.content.trim(),
      });
      alert(t('common.partner_alert_submitted'));
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : t('common.partner_alert_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose} padding="p-5 sm:p-8" gap="gap-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-mono-900">{t('common.partner_apply_title')}</h2>
        <ModalCloseButton onClick={onClose} />
      </div>

      <Select
        value={form.type}
        onChange={(val) => setForm((prev) => ({ ...prev, type: val }))}
        placeholder={t('common.partner_type_select_label')}
        options={partnerTypes}
      />

      <TextBox
        label={t('auth.name')}
        placeholder={t('auth.name_placeholder')}
        value={form.name}
        onChange={update('name')}
      />

      <TextBox
        label={t('common.partner_company_optional')}
        placeholder={t('common.partner_company_placeholder')}
        value={form.company}
        onChange={update('company')}
      />

      <TextBox
        variant="phone"
        label={t('auth.phone')}
        placeholder={t('auth.phone_placeholder')}
        value={form.phone}
        onChange={handlePhoneChange}
      />

      <TextBox
        label={t('auth.email')}
        placeholder={t('common.partner_email_placeholder')}
        value={form.email}
        onChange={update('email')}
      />

      <TextBox
        variant="textarea"
        label={t('common.partner_inquiry_required')}
        placeholder={t('common.partner_inquiry_placeholder')}
        value={form.content}
        onChange={update('content')}
        rows={4}
        required
      />

      {/* 개인정보 수집·이용 동의 (필수) */}
      <div className="border-t border-mono-100 pt-4 space-y-1.5">
        <CheckBox
          label={t('info.inquiry_privacy_consent')}
          checked={privacyAgreed}
          onChange={() => setPrivacyAgreed((v) => !v)}
        />
        <div className="ml-9 text-xs text-mono-500 leading-relaxed break-keep">
          {t('info.inquiry_privacy_consent_desc')}{' '}
          <Link to="/policy/privacyConsent" target="_blank" rel="noreferrer" className="text-orange-500 underline">
            {t('info.inquiry_privacy_view')}
          </Link>
        </div>
      </div>

      <div className="flex gap-3">
        <Button level="level3" fullWidth onClick={onClose}>{t('common.cancel')}</Button>
        <Button level="level1" fullWidth onClick={handleApply} disabled={submitting}>
          {submitting ? t('common.submitting') : t('common.apply_now')}
        </Button>
      </div>
    </ModalWrapper>
  );
}
